/**
 * PITCH-AWARE FIT (B4, spec §D.11). MapLibre's cameraForBounds ignores pitch (issue #8479), so this
 * fits a point set with MapLibre's own camera model:
 *
 *   - a pinhole camera, vertical fov 36.87°, at cameraToCenterDistance d = 0.5 / tan(fov/2) · height
 *     from the centre, looking down at `pitch`;
 *   - the principal point (centre of perspective) is the centre of the padded frame: MapLibre shears its
 *     projection matrix by the padding offset (m[8] = −offset.x·2/w, m[9] = offset.y·2/h);
 *   - flat ground (the map is locked to Mercator with no terrain).
 *
 * For a ground offset (gx right, gy forward) in CSS px at the centre's zoom:
 *   depth = d + gy·sin p,   x = d·gx / depth,   y_up = d·gy·cos p / depth
 *
 * Algorithm: rotate the points into the target bearing, fit their bbox flat, then Newton-style corrections:
 * move the centre to the ground point under the screen bbox's midpoint and rescale by the bbox overflow,
 * until the projected bbox sits centred and tight in the padded frame (2–4 rounds; converges to < 0.05 px).
 *
 * Pure (no map, no DOM): unit-tested in fit.test.ts, and checked in the browser against map.project().
 */
import type { LngLat } from "@/contracts/data";
import type { Insets } from "@/contracts/store";

export const FOV_DEG = 36.87; //  MapLibre's default vertical field of view
export const TILE = 512; //       MapLibre zoom is in 512 px tiles
const RAD = Math.PI / 180;

/* ── mercator (0…1 world units, y down) ────────────────────────────── */

export function toMerc([lng, lat]: LngLat): [number, number] {
  const s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * RAD);
  return [(lng + 180) / 360, 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)];
}
export function fromMerc([x, y]: [number, number]): LngLat {
  return [x * 360 - 180, (360 / Math.PI) * Math.atan(Math.exp((180 - y * 360) * RAD)) - 90];
}

/* ── the camera model ──────────────────────────────────────────────── */

export interface View {
  width: number;
  height: number;
  padding: Insets;
  fovDeg?: number;
}
export interface PinholeCamera {
  center: LngLat;
  zoom: number;
  bearing: number; // degrees, MapLibre convention (the compass direction that points up)
  pitch: number; //   degrees
}

/** MapLibre's cameraToCenterDistance for a viewport height. */
export const cameraDistance = (height: number, fovDeg = FOV_DEG): number => (0.5 / Math.tan((fovDeg * RAD) / 2)) * height;

/** The padded frame: principal point and inner size. */
export function frameOf(v: View): { px: number; py: number; w: number; h: number } {
  const p = v.padding;
  const w = Math.max(1, v.width - p.left - p.right);
  const h = Math.max(1, v.height - p.top - p.bottom);
  return { px: p.left + w / 2, py: p.top + h / 2, w, h };
}

/** Ground offset (east, north) in mercator units → (right, forward) for a bearing. */
function rotate(e: number, n: number, cosB: number, sinB: number): [number, number] {
  return [e * cosB - n * sinB, n * cosB + e * sinB];
}
function unrotate(u: number, v: number, cosB: number, sinB: number): [number, number] {
  return [u * cosB + v * sinB, v * cosB - u * sinB];
}

/**
 * Screen position (CSS px, top-left origin) of a geographic point under the pinhole model, or null when the
 * point is behind the camera. Matches map.project() for a flat Mercator map (to float precision).
 */
export function projectPinhole(pt: LngLat, cam: PinholeCamera, view: View): [number, number] | null {
  const d = cameraDistance(view.height, view.fovDeg);
  const { px, py } = frameOf(view);
  const S = TILE * 2 ** cam.zoom;
  const [cx, cy] = toMerc(cam.center);
  const [mx, my] = toMerc(pt);
  const b = cam.bearing * RAD;
  const [u, v] = rotate((mx - cx) * S, -(my - cy) * S, Math.cos(b), Math.sin(b));
  const p = cam.pitch * RAD;
  const depth = d + v * Math.sin(p);
  if (depth <= 1e-6) return null;
  return [px + (d * u) / depth, py - (d * v * Math.cos(p)) / depth];
}

/** Ground offset (right, forward) in px under a screen offset from the principal point (x right, y up). */
function unprojectOffset(x: number, yUp: number, d: number, cosP: number, sinP: number): [number, number] {
  const den = d * cosP - yUp * sinP;
  const gy = den > 1e-6 ? (yUp * d) / den : (yUp * d) / 1e-6;
  const gx = (x * (d + gy * sinP)) / d;
  return [gx, gy];
}

/* ── the fit ───────────────────────────────────────────────────────── */

export interface FitOptions {
  bearing: number;
  pitch: number;
  view: View;
  minZoom?: number;
  maxZoom?: number;
  /** Correction rounds after the flat fit (default 8; converged fits stop early). */
  iterations?: number;
  /** Extra room (CSS px) around individual points: a pin's disc and rings, the site mark and its label. */
  margins?: ArrayLike<number>;
}
export interface FitResult {
  center: LngLat;
  zoom: number;
  bearing: number;
  pitch: number;
  /** Correction rounds used. */
  rounds: number;
  /** Projected bbox / frame (1 = tight) after the last round, before any zoom clamp. */
  overflow: number;
  /** True when the zoom hit minZoom or maxZoom (the content is centred, not tight). */
  clamped: boolean;
}

interface Extents {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Margins of the points that set each edge. */
  mL: number;
  mR: number;
  mB: number;
  mT: number;
}

/**
 * Centre and zoom that fit every point inside the padded frame at the given bearing and pitch.
 * Degenerate input (no points, one point, zero extent) falls back to the point(s)' centre at maxZoom.
 */
export function fitPitched(points: readonly LngLat[], o: FitOptions): FitResult {
  const minZ = o.minZoom ?? 0;
  const maxZ = o.maxZoom ?? 22;
  const bearing = o.bearing;
  const pitch = Math.max(0, Math.min(85, o.pitch));
  if (!points.length) return { center: [0, 0], zoom: minZ, bearing, pitch, rounds: 0, overflow: 1, clamped: true };

  const b = bearing * RAD;
  const cosB = Math.cos(b);
  const sinB = Math.sin(b);
  const p = pitch * RAD;
  const cosP = Math.cos(p);
  const sinP = Math.sin(p);
  const d = cameraDistance(o.view.height, o.view.fovDeg);
  const frame = frameOf(o.view);

  // Rotated mercator coordinates relative to the first point (keeps magnitudes small).
  const ref = toMerc(points[0]);
  const U = new Float64Array(points.length);
  const V = new Float64Array(points.length);
  let u0 = Infinity;
  let u1 = -Infinity;
  let v0 = Infinity;
  let v1 = -Infinity;
  points.forEach((pt, i) => {
    const m = toMerc(pt);
    const [u, v] = rotate(m[0] - ref[0], -(m[1] - ref[1]), cosB, sinB);
    U[i] = u;
    V[i] = v;
    if (u < u0) u0 = u;
    if (u > u1) u1 = u;
    if (v < v0) v0 = v;
    if (v > v1) v1 = v;
  });

  const toLngLat = (cu: number, cv: number): LngLat => {
    const [e, n] = unrotate(cu, cv, cosB, sinB);
    return fromMerc([ref[0] + e, ref[1] - n]);
  };
  const zoomOf = (S: number): number => Math.log2(S / TILE);
  const du = u1 - u0;
  const dv = v1 - v0;
  let cu = (u0 + u1) / 2;
  let cv = (v0 + v1) / 2;
  if (!(du > 1e-12 || dv > 1e-12)) {
    return { center: toLngLat(cu, cv), zoom: maxZ, bearing, pitch, rounds: 0, overflow: 0, clamped: true };
  }

  // Flat fit (the forward axis foreshortened by cos p as a first guess).
  const sx = du > 1e-12 ? frame.w / du : Infinity;
  const sy = dv > 1e-12 ? frame.h / (dv * Math.max(0.2, cosP)) : Infinity;
  let S = Math.min(sx, sy);

  const M = o.margins;
  const extents = (): Extents => {
    const e: Extents = { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity, mL: 0, mR: 0, mB: 0, mT: 0 };
    for (let i = 0; i < U.length; i++) {
      const gx = (U[i] - cu) * S;
      const gy = (V[i] - cv) * S;
      const depth = Math.max(d * 0.05, d + gy * sinP);
      const x = (d * gx) / depth;
      const y = (d * gy * cosP) / depth; // up
      const m = M ? Math.max(0, M[i] ?? 0) : 0;
      if (x - m < e.x0) {
        e.x0 = x - m;
        e.mL = m;
      }
      if (x + m > e.x1) {
        e.x1 = x + m;
        e.mR = m;
      }
      if (y - m < e.y0) {
        e.y0 = y - m;
        e.mB = m;
      }
      if (y + m > e.y1) {
        e.y1 = y + m;
        e.mT = m;
      }
    }
    return e;
  };
  /** How much the content (margins excluded, as they do not scale) overflows the room the margins leave. */
  const overflowOf = (e: Extents): number => {
    const mx = e.mL + e.mR;
    const my = e.mB + e.mT;
    return Math.max((e.x1 - e.x0 - mx) / Math.max(1, frame.w - mx), (e.y1 - e.y0 - my) / Math.max(1, frame.h - my));
  };
  const recentre = (): Extents => {
    const e = extents();
    const [gx, gy] = unprojectOffset((e.x0 + e.x1) / 2, (e.y0 + e.y1) / 2, d, cosP, sinP);
    cu += gx / S;
    cv += gy / S;
    return extents();
  };

  const rounds = Math.max(1, o.iterations ?? 8);
  let used = 0;
  for (let i = 0; i < rounds; i++) {
    used = i + 1;
    const e = recentre();
    const overflow = overflowOf(e);
    if (!(overflow > 0)) break;
    S /= overflow;
    const mid = extents();
    const off = Math.max(Math.abs((mid.x0 + mid.x1) / 2), Math.abs((mid.y0 + mid.y1) / 2));
    if (Math.abs(overflow - 1) < 1e-6 && off < 0.05) break;
  }
  // The last rescale moved the midpoint a little: one more recentre keeps it centred.
  const last = recentre();
  const overflow = Math.max((last.x1 - last.x0) / frame.w, (last.y1 - last.y0) / frame.h);

  let zoom = zoomOf(S);
  let clamped = false;
  if (zoom > maxZ || zoom < minZ) {
    clamped = true;
    zoom = Math.max(minZ, Math.min(maxZ, zoom));
    S = TILE * 2 ** zoom;
    recentre();
  }
  return { center: toLngLat(cu, cv), zoom, bearing, pitch, rounds: used, overflow, clamped };
}

/* ── camera bounds (MapLibre's maxBounds) ──────────────────────────── */

export type BBox = readonly [number, number, number, number]; // w, s, e, n

/**
 * MapLibre's defaultConstrain for Mercator with maxBounds: the zoom rises until the bounds cover the whole
 * viewport, then the centre moves so a symmetric w/2 × h/2 box around it stays inside. It ignores padding,
 * pitch and bearing, which is why a fit near the region's edge can be pushed sideways.
 */
export function constrainCamera(center: LngLat, zoom: number, b: BBox, width: number, height: number): { center: LngLat; zoom: number } {
  const [bx0, by1] = toMerc([b[0], b[1]]);
  const [bx1, by0] = toMerc([b[2], b[3]]);
  const S = TILE * 2 ** zoom;
  const [x, y] = toMerc(center);
  // bounds narrower than the screen: centre that axis and zoom in until they cover it (no further clamp)
  const bw = (bx1 - bx0) * S;
  const bh = (by1 - by0) * S;
  const sx = bw < width ? width / bw : 0;
  const sy = bh < height ? height / bh : 0;
  const sc = Math.max(sx, sy);
  if (sc) return { center: fromMerc([sx ? (bx0 + bx1) / 2 : x, sy ? (by0 + by1) / 2 : y]), zoom: zoom + Math.log2(sc) };
  const hw = width / 2 / S;
  const hh = height / 2 / S;
  let cx = x;
  let cy = y;
  if (y - hh < by0) cy = by0 + hh;
  if (y + hh > by1) cy = by1 - hh;
  if (x - hw < bx0) cx = bx0 + hw;
  if (x + hw > bx1) cx = bx1 - hw;
  return { center: cx === x && cy === y ? center : fromMerc([cx, cy]), zoom };
}

const insideFrame = (b: { x0: number; y0: number; x1: number; y1: number } | null, v: View, tol = 0.75): boolean =>
  !!b && b.x0 >= v.padding.left - tol && b.y0 >= v.padding.top - tol && b.x1 <= v.width - v.padding.right + tol && b.y1 <= v.height - v.padding.bottom + tol;

/**
 * How far (px) points spill out of the padded frame: the sum over the four edges, plus the last point's
 * own spill twice more (the destination pin matters most). 0 = everything inside.
 */
export function frameSpill(points: readonly LngLat[], cam: PinholeCamera, v: View, margins?: ArrayLike<number>): number {
  const b = projectedBounds(points, cam, v, margins);
  if (!b) return Infinity;
  const L = v.padding.left;
  const T = v.padding.top;
  const R = v.width - v.padding.right;
  const B = v.height - v.padding.bottom;
  const over = (x0: number, y0: number, x1: number, y1: number) => Math.max(0, L - x0) + Math.max(0, T - y0) + Math.max(0, x1 - R) + Math.max(0, y1 - B);
  const last = points.length - 1;
  const dest = projectPinhole(points[last], cam, v);
  const dm = margins ? Math.max(0, margins[last] ?? 0) : 0;
  return over(b.x0, b.y0, b.x1, b.y1) + (dest ? 2 * over(dest[0] - dm, dest[1] - dm, dest[0] + dm, dest[1] + dm) : 0);
}

/** How far (px) one point with its margin spills out of the padded frame (the sum over the four edges). */
export function pointSpill(pt: LngLat, cam: PinholeCamera, v: View, margin = 0): number {
  const s = projectPinhole(pt, cam, v);
  if (!s) return 1e6;
  const m = Math.max(0, margin);
  const L = v.padding.left;
  const T = v.padding.top;
  const R = v.width - v.padding.right;
  const B = v.height - v.padding.bottom;
  return Math.max(0, L - (s[0] - m)) + Math.max(0, T - (s[1] - m)) + Math.max(0, s[0] + m - R) + Math.max(0, s[1] + m - B);
}

export interface BoundedFitOptions extends FitOptions {
  bounds?: BBox;
  step?: number;
  /**
   * Indices of points that must stay inside the padded frame (the site and the destination pin, L1-01):
   * the route line is soft beside them. With `hard`, a fit the bounds push out of the frame is searched
   * over zoom, centre and pitch (`pitchRange`) before any hard point may leave the frame.
   */
  hard?: readonly number[];
  /** The pitches (degrees) the search may use when the given pitch cannot hold the hard points. */
  pitchRange?: readonly [number, number];
}

export interface BoundedFit extends FitResult {
  /** The bounds moved the centre or raised the zoom. */
  constrained: boolean;
  /** Every point is inside the padded frame once MapLibre has applied its bounds. */
  inside: boolean;
  /** Every hard point is inside (true when there are none). */
  hardInside?: boolean;
}

/**
 * fitPitched, then MapLibre's bounds applied. When the constraint would push the route out of the frame (a
 * destination near the region's edge: the viewport's far side would leave the bounds), search the zoom
 * (±2 levels around the fit) for the highest zoom at which the constrained camera keeps every point inside.
 * When no zoom can (the route is larger than the region view allows, or the land beyond the destination is
 * narrower than the chrome beside the frame), take the camera that spills least, the destination weighted.
 * With `hard` points the search is the constrained one (fitHard): they stay inside whenever any camera
 * within the bounds and the pitch range can hold them.
 */
export function fitInBounds(points: readonly LngLat[], o: BoundedFitOptions): BoundedFit {
  const f = fitPitched(points, o);
  const view = o.view;
  if (!o.bounds || !points.length) return { ...f, constrained: false, inside: true, hardInside: true };
  const cam = (center: LngLat, zoom: number): PinholeCamera => ({ center, zoom, bearing: f.bearing, pitch: f.pitch });
  const first = constrainCamera(f.center, f.zoom, o.bounds, view.width, view.height);
  const moved = first.zoom !== f.zoom || first.center !== f.center;
  const firstIn = insideFrame(projectedBounds(points, cam(first.center, first.zoom), view, o.margins), view);
  if (firstIn) return { ...f, center: first.center, zoom: first.zoom, constrained: moved, inside: true, hardInside: true };
  if (o.hard?.length) return fitHard(points, o, f);
  if (!moved) return { ...f, constrained: false, inside: firstIn };
  const bounds = o.bounds;
  // Coarse pass on a thinned copy (≤ 96 points, the destination kept), then a fine pass on every point.
  const k = Math.max(1, Math.ceil(points.length / 96));
  const idx: number[] = [];
  for (let i = 0; i < points.length; i += k) idx.push(i);
  if (idx[idx.length - 1] !== points.length - 1) idx.push(points.length - 1);
  const coarseP = idx.map((i) => points[i]);
  const coarseM = o.margins ? idx.map((i) => o.margins![i] ?? 0) : undefined;
  const evalAt = (z: number, P: readonly LngLat[], M: ArrayLike<number> | undefined): { fit: BoundedFit; spill: number } => {
    const g = fitPitched(P, { ...o, margins: M, minZoom: z, maxZoom: z, iterations: 5 });
    const c = constrainCamera(g.center, z, bounds, view.width, view.height);
    const spill = frameSpill(P, cam(c.center, c.zoom), view, M);
    return { fit: { ...g, center: c.center, zoom: c.zoom, constrained: true, inside: spill <= 0.75 }, spill };
  };
  const coarse = o.step ?? 0.1;
  const lo = Math.max(o.minZoom ?? 0, f.zoom - 2);
  const hi = Math.min(o.maxZoom ?? 22, f.zoom + 2);
  let insideZ: number | null = null;
  let bestZ = first.zoom;
  let bestSpill = frameSpill(coarseP, cam(first.center, first.zoom), view, coarseM);
  for (let z = hi; z >= lo - 1e-9; z -= coarse) {
    const { spill } = evalAt(z, coarseP, coarseM);
    if (spill <= 0.75 && insideZ === null) insideZ = z;
    if (spill < bestSpill - 1e-6) {
      bestSpill = spill;
      bestZ = z;
    }
  }
  // Fine pass (every point, 0.01 zoom) around the coarse answer.
  let best: { fit: BoundedFit; spill: number } | null = null;
  const from = (insideZ ?? bestZ) + coarse;
  const to = (insideZ ?? bestZ) - coarse;
  for (let z = Math.min(hi, from); z >= Math.max(lo, to) - 1e-9; z -= 0.01) {
    const e = evalAt(z, points, o.margins);
    if (insideZ !== null && e.fit.inside) return e.fit; // the highest zoom that fits
    if (!best || e.spill < best.spill - 1e-6) best = e;
  }
  if (best) return best.fit;
  return { ...f, center: first.center, zoom: first.zoom, constrained: true, inside: false };
}

/** How far (px) the bbox of points (with margins) spills out of the padded frame; 0 = inside. */
function bboxSpill(points: readonly LngLat[], cam: PinholeCamera, v: View, margins?: ArrayLike<number>): number {
  const b = projectedBounds(points, cam, v, margins);
  if (!b) return 1e6;
  return Math.max(0, v.padding.left - b.x0) + Math.max(0, v.padding.top - b.y0) + Math.max(0, b.x1 - (v.width - v.padding.right)) + Math.max(0, b.y1 - (v.height - v.padding.bottom));
}

const HARD_TOL = 0.75;
/** Route spill (px) a degree away from the §D.11 pitch is worth: the nearest pitch that frames wins. */
const PITCH_COST_PX = 1.5;
const PITCH_STEP = 2;
/** The centre search: pattern steps (px) and the moves allowed at each. */
const NUDGE_STEPS = [64, 32, 16, 8, 4, 2, 1] as const;
const NUDGE_MOVES = 10;
const NUDGES: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

interface HardCand {
  center: LngLat;
  zoom: number;
  pitch: number;
  /** px the hard points spill (with their margins) */
  hard: number;
  /** px the thinned point set's bbox spills; Infinity while the hard points are out (not measured) */
  soft: number;
}

/**
 * The constrained search behind fitInBounds({ hard }) (L1-01; §D.11, §E.1, §A.6): select frames the route,
 * the pin and the site inside the card-open frame. Pitches are tried nearest the given (§D.11) one first,
 * in 2° steps inside `pitchRange`; the bearing never changes. At each pitch the zoom runs down (0.1 steps)
 * from the highest zoom at which the hard points alone still fit, never below the bounds' floor; at each
 * zoom the centre starts from the fit of every point and from the fit of the hard points, after MapLibre's
 * bounds, and is nudged within the bounds (pattern search, 64 → 1 px): hard spill first, route spill next.
 * The winner keeps the hard points inside, then spills the route least (the highest zoom that holds it all
 * wins outright), then stays nearest the §D.11 pitch. When no camera can hold the hard points, the one
 * that spills them least.
 */
function fitHard(points: readonly LngLat[], o: BoundedFitOptions, f: FitResult): BoundedFit {
  const view = o.view;
  const bounds = o.bounds!;
  const W = view.width;
  const Hh = view.height;
  const M = o.margins;
  const mOf = (i: number): number => (M ? Math.max(0, M[i] ?? 0) : 0);
  const hard = [...new Set((o.hard ?? []).filter((i) => i >= 0 && i < points.length))];
  // a thinned copy (≤ 48 points) for the search, every hard point kept
  const k = Math.max(1, Math.ceil(points.length / 48));
  const idx: number[] = [];
  for (let i = 0; i < points.length; i += k) idx.push(i);
  if (idx[idx.length - 1] !== points.length - 1) idx.push(points.length - 1);
  for (const h of hard) if (!idx.includes(h)) idx.push(h);
  const P = idx.map((i) => points[i]);
  const PM = M ? idx.map(mOf) : undefined;
  const HP = hard.map((i) => points[i]);
  const HM = hard.map(mOf);
  const at = (center: LngLat, zoom: number, pitch: number): PinholeCamera => ({ center, zoom, bearing: f.bearing, pitch });
  const ok = (c: { hard: number }): boolean => c.hard <= HARD_TOL;
  const measure = (center: LngLat, zoom: number, pitch: number): HardCand => {
    const c = at(center, zoom, pitch);
    let hs = 0;
    for (let j = 0; j < HP.length; j++) hs += pointSpill(HP[j], c, view, HM[j]);
    return { center, zoom, pitch, hard: hs, soft: hs <= HARD_TOL ? bboxSpill(P, c, view, PM) : Infinity };
  };
  /** a beats b at one zoom and pitch: the hard points first, then the route. */
  const beats = (a: HardCand, b: HardCand): boolean => (ok(a) !== ok(b) ? ok(a) : !ok(a) ? a.hard < b.hard - 1e-6 : a.soft < b.soft - 1e-6);

  const solveAt = (pitch: number, z: number): HardCand | null => {
    const starts: LngLat[] = [
      fitPitched(P, { ...o, pitch, margins: PM, minZoom: z, maxZoom: z, iterations: 4 }).center,
      fitPitched(HP, { ...o, pitch, margins: HM, minZoom: z, maxZoom: z, iterations: 4 }).center,
    ];
    let best: HardCand | null = null;
    for (const s0 of starts) {
      const c0 = constrainCamera(s0, z, bounds, W, Hh);
      if (c0.zoom > z + 1e-9) return null; // below the bounds' floor: MapLibre would raise it
      const m = measure(c0.center, z, pitch);
      if (!best || beats(m, best)) best = m;
    }
    if (!best || (ok(best) && best.soft <= HARD_TOL)) return best;
    const S = TILE * 2 ** z;
    for (const step of NUDGE_STEPS) {
      for (let moves = 0; moves < NUDGE_MOVES; moves++) {
        const mc = toMerc(best.center);
        let moved = false;
        for (const [dx, dy] of NUDGES) {
          const c = constrainCamera(fromMerc([mc[0] + (dx * step) / S, mc[1] + (dy * step) / S]), z, bounds, W, Hh).center;
          const s = measure(c, z, pitch);
          if (beats(s, best)) {
            best = s;
            moved = true;
            break;
          }
        }
        if (!moved) break;
      }
    }
    return best;
  };

  const within = (c: HardCand): boolean => ok(c) && c.soft <= HARD_TOL;
  const p0 = f.pitch;
  const cost = (c: HardCand): number => c.soft + PITCH_COST_PX * Math.abs(c.pitch - p0);
  const better = (a: HardCand, b: HardCand | null): boolean => {
    if (!b) return true;
    if (ok(a) !== ok(b)) return ok(a);
    if (!ok(a)) return a.hard < b.hard - 1e-6;
    if (Math.abs(cost(a) - cost(b)) > 1e-6) return cost(a) < cost(b);
    return a.zoom > b.zoom;
  };

  const [pLo, pHi] = o.pitchRange ?? [p0, p0];
  const pitches = [p0];
  for (let d = PITCH_STEP; p0 - d >= Math.min(pLo, p0) - 1e-9 || p0 + d <= Math.max(pHi, p0) + 1e-9; d += PITCH_STEP) {
    if (p0 - d >= pLo - 1e-9) pitches.push(p0 - d);
    if (p0 + d <= pHi + 1e-9) pitches.push(p0 + d);
  }
  // the range's own ends, when the 2° steps stop short of them
  for (const e of [pLo, pHi]) if (Math.abs(e - p0) > 1e-9 && !pitches.some((p) => Math.abs(p - e) < 0.5)) pitches.push(e);
  const zHi = Math.min(o.maxZoom ?? 22, f.zoom + 2);
  const zLo = Math.max(o.minZoom ?? 0, f.zoom - 2);
  let best: HardCand | null = null;
  for (const pitch of pitches) {
    // above this zoom the hard points alone no longer fit the frame, wherever the centre is
    const cap = HP.length > 1 ? fitPitched(HP, { ...o, pitch, margins: HM, minZoom: 0, maxZoom: 22 }).zoom : zHi;
    const top = Math.min(zHi, Math.max(zLo, cap));
    let atPitch: HardCand | null = null;
    for (let z = top; z >= zLo - 1e-9; z -= 0.1) {
      const c = solveAt(pitch, z);
      if (!c) break; // lower zooms are below the floor too
      if (better(c, atPitch)) atPitch = c;
      if (within(c)) break; // the highest zoom that holds everything at this pitch
    }
    if (!atPitch) continue;
    if (within(atPitch)) {
      // a little higher, in 0.01 steps, while everything still fits
      for (let z = atPitch.zoom + 0.01; z < Math.min(top, atPitch.zoom + 0.1) - 1e-9; z += 0.01) {
        const c = solveAt(pitch, z);
        if (!c || !within(c)) break;
        atPitch = c;
      }
    } else {
      for (let z = atPitch.zoom + 0.05; z >= atPitch.zoom - 0.05 - 1e-9; z -= 0.025) {
        if (z > top + 1e-9 || z < zLo - 1e-9) continue;
        const c = solveAt(pitch, z);
        if (c && better(c, atPitch)) atPitch = c;
      }
    }
    if (better(atPitch, best)) best = atPitch;
    if (within(atPitch)) break; // nearer pitches were tried first
  }
  if (!best) {
    const c = constrainCamera(f.center, f.zoom, bounds, W, Hh);
    return { ...f, center: c.center, zoom: c.zoom, constrained: true, inside: false, hardInside: false };
  }
  const camF = at(best.center, best.zoom, best.pitch);
  let hs = 0;
  for (const h of hard) hs += pointSpill(points[h], camF, view, mOf(h));
  const inside = insideFrame(projectedBounds(points, camF, view, M), view);
  return { ...f, center: best.center, zoom: best.zoom, pitch: best.pitch, constrained: true, inside, hardInside: hs <= HARD_TOL };
}

/** Screen bbox of points under a camera (pinhole), for checks and the harness readout. */
export function projectedBounds(
  points: readonly LngLat[],
  cam: PinholeCamera,
  view: View,
  margins?: ArrayLike<number>,
): { x0: number; y0: number; x1: number; y1: number } | null {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < points.length; i++) {
    const s = projectPinhole(points[i], cam, view);
    if (!s) return null;
    const m = margins ? Math.max(0, margins[i] ?? 0) : 0;
    if (s[0] - m < x0) x0 = s[0] - m;
    if (s[0] + m > x1) x1 = s[0] + m;
    if (s[1] - m < y0) y0 = s[1] - m;
    if (s[1] + m > y1) y1 = s[1] + m;
  }
  return Number.isFinite(x0) ? { x0, y0, x1, y1 } : null;
}

/** Grows every padding edge by `px` (room for pins and labels at the frame's edge), keeping ≥ 64 px of frame. */
export function inflate(p: Insets, px: number, width: number, height: number): Insets {
  const room = (a: number, b: number, total: number): [number, number] => {
    const extra = Math.max(0, Math.min(px, (total - a - b - 64) / 2));
    return [a + extra, b + extra];
  };
  const [left, right] = room(p.left, p.right, width);
  const [top, bottom] = room(p.top, p.bottom, height);
  return { top, right, bottom, left };
}
